import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useContext } from "react";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { capitalizeWord } from "~/shared/utils/string-fns.ts";
import {
	formatTimeToString,
	parseTimeString,
} from "~/shared/utils/time-utils.ts";

import { getAddressTypeLabel } from "../utils/address-helpers.ts";

type HoursAndNumberRowProps = {
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	onClearFieldError: (field: string) => void;
};

export function HoursAndNumberRow({
	type,
	hasFieldError,
	onClearFieldError,
}: Readonly<HoursAndNumberRowProps>) {
	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem =
		selectedAddressBookItems[type as keyof SelectedAddressBookItems];
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const addressLabel = getAddressTypeLabel(type);
	const stopType = type === "pickup" ? 1 : 2;
	const detail = addressBookItem?.details.find((d) => d.stopType === stopType);

	return (
		<div className={"pickup-delivery-row"}>
			<div className={"pickup-delivery-hours-from"}>
				<Label editorId={`${type}HoursFrom`}>
					{addressLabel} Hours From <span className={"required"}>*</span>
				</Label>
				<TimePicker
					id={`${type}HoursFrom`}
					name={`${type}HoursFrom`}
					onChange={(e) => {
						onClearFieldError(`${type}HoursFrom`);
						updateSelectedAddressBookItem(
							type,
							"hoursOpen",
							formatTimeToString(e.value),
						);
					}}
					placeholder={""}
					valid={!hasFieldError(`${type}HoursFrom`)}
					value={parseTimeString(detail?.hoursOpen || null)}
				/>
				{hasFieldError(`${type}HoursFrom`) ? <Error>Required</Error> : null}
			</div>
			<div className={"pickup-delivery-hours-to"}>
				<Label editorId={`${type}HoursTo`}>
					{addressLabel} Hours To <span className={"required"}>*</span>
				</Label>
				<TimePicker
					id={`${type}HoursTo`}
					name={`${type}HoursTo`}
					onChange={(e) => {
						onClearFieldError(`${type}HoursTo`);
						updateSelectedAddressBookItem(
							type,
							"hoursClose",
							formatTimeToString(e.value),
						);
					}}
					placeholder={""}
					valid={!hasFieldError(`${type}HoursTo`)}
					value={parseTimeString(detail?.hoursClose || null)}
				/>
				{hasFieldError(`${type}HoursTo`) ? <Error>Required</Error> : null}
			</div>
			<div className={"pickup-delivery-number"}>
				<Label editorId={`${type}Number`}>{capitalizeWord(type)} #</Label>
				<TextBox
					id={`${type}Number`}
					name={`${type}Number`}
					onChange={(e) =>
						onChange(
							type == "pickup" ? "puNumber" : "deliveryPO",
							e.target.value as string,
						)
					}
					value={
						type == "pickup"
							? (quoteState.puNumber ?? "")
							: (quoteState.deliveryPO ?? "")
					}
				/>
			</div>
		</div>
	);
}
